import { type ReactElement, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Spinner } from "../Spinner";

export type InputImageTileProps = {
  value: string | null;
  onChange?: (url: string | null) => void;
  uploadFile: (file: File) => Promise<string>;
  className?: string;
};

export const InputImageTile = ({
  value,
  onChange,
  uploadFile,
  className,
}: InputImageTileProps): ReactElement => {
  const [url, setUrl] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local preview when the controlled value changes (adjust state during render).
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (value !== prevValue) {
    setPrevValue(value);
    setUrl(value);
  }

  const setFile = async (file: File | undefined) => {
    setError("");
    if (!file) return;

    setUploading(true);

    try {
      const imageUrl = await uploadFile(file);
      setUrl(imageUrl);
      onChange?.(imageUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Upload failed");
    }

    setUploading(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const removeImage = () => {
    setUrl(null);
    onChange?.(null);
  };

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-lg",
        !url && "border border-dashed border-zinc-300 bg-zinc-50",
        className,
      )}
    >
      <div className="relative pt-[56.25%]">
        <div className="absolute inset-0 flex h-full w-full items-center justify-center">
          {error && (
            <span className="pointer-events-none absolute right-1 bottom-1 left-1 z-10 rounded-md border border-zinc-300 bg-zinc-50 px-2 text-red-500">
              {error}
            </span>
          )}
          {uploading ? (
            <Spinner />
          ) : url ? (
            <img
              alt="Uploaded preview"
              src={url}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
        <div className="absolute inset-0 flex w-full items-center justify-center">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              void setFile(event.target.files?.[0]);
            }}
          />
          {!url && !uploading && (
            <Button
              aria-label="Upload image"
              color="primary"
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-full bg-zinc-400 text-white"
              size="small"
            >
              <Icon icon="PLUS" />
            </Button>
          )}
          {url && (
            <Button
              aria-label="Remove image"
              color="secondary"
              type="button"
              onClick={removeImage}
              size="small"
              className="absolute top-1 right-1 rounded-sm p-1"
            >
              <Icon icon="CLOSE" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
